import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { DeveloperDocumentsService, DeveloperDocumentType, DeveloperProjectDocument } from '../services/developer-documents.service';
import { useNotification } from '../context/NotificationContext';
import { resolveFileUrl } from '../utils/resolveFileUrl';
import styles from './DeveloperProjectDocumentsPage.module.css';

export default function DeveloperProjectDocumentsPage() {
  const { id } = useParams<{ id: string }>();
  const { addNotification } = useNotification();
  const [types, setTypes] = useState<DeveloperDocumentType[]>([]);
  const [documents, setDocuments] = useState<DeveloperProjectDocument[]>([]);
  const [typeId, setTypeId] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  const [customLabel, setCustomLabel] = useState('');
  const [loading, setLoading] = useState(true);
  const selectedDoc = documents.find(d => d.type.id === typeId);
  const selectedBlocked = selectedDoc?.status === 'APPROVED';
  const selectedType = types.find(t => t.id === typeId);
  const isOptionalType = (type?: DeveloperDocumentType | null) => {
    const code = String(type?.code || '').trim().toUpperCase();
    const label = String(type?.label || '').trim().toLowerCase();
    return code === 'OTHER' || code.startsWith('OTHER_') || label.includes('otro');
  };
  const documentByType = new Map(documents.map(doc => [doc.type.id, doc]));
  const requiredTypes = types.filter(type => !isOptionalType(type));
  const missingTypes = requiredTypes.filter(type => !documentByType.has(type.id));
  const submittedCount = requiredTypes.length - missingTypes.length;
  const isOtherType = Boolean(selectedType && isOptionalType(selectedType));
  const requiredDocsReady = requiredTypes.length > 0 && missingTypes.length === 0;

  const statusLabels: Record<string, string> = {
    UPLOADED: 'Cargado',
    IN_REVIEW: 'En revisión',
    OBSERVED: 'Observado',
    APPROVED: 'Aprobado',
  };
  const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx'];
  const allowedAccept = '.pdf,.doc,.docx,.xls,.xlsx';

  const validateFile = (nextFile: File | null) => {
    if (!nextFile) return null;
    const ext = nextFile.name.split('.').pop()?.toLowerCase() || '';
    if (!allowedExtensions.includes(ext)) {
      addNotification('Formato no permitido. Sube un archivo PDF, Word o Excel.', 'error');
      return null;
    }
    return nextFile;
  };

  const statusClass = (status?: string) => {
    const key = (status || '').toUpperCase();
    if (key === 'APPROVED') return `${styles.statusBadge} ${styles.statusApproved}`;
    if (key === 'IN_REVIEW') return `${styles.statusBadge} ${styles.statusInReview}`;
    if (key === 'OBSERVED') return `${styles.statusBadge} ${styles.statusObserved}`;
    if (key === 'UPLOADED') return `${styles.statusBadge} ${styles.statusUploaded}`;
    return styles.statusBadge;
  };

  const load = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [t, d] = await Promise.all([
        DeveloperDocumentsService.listTypes(),
        DeveloperDocumentsService.listDocuments(id)
      ]);
      setTypes(Array.isArray(t) ? t : []);
      setDocuments(Array.isArray(d) ? d : []);
    } catch (error: any) {
      addNotification(error?.message || 'Error cargando documentos', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onUpload = async () => {
    if (!id || !typeId || !file) return;
    if (isOtherType && !customLabel.trim()) {
      addNotification('Especifica el documento para "Otros documentos".', 'error');
      return;
    }
    try {
      await DeveloperDocumentsService.uploadDocument(id, typeId, file, customLabel.trim() || undefined);
      setFile(null);
      setCustomLabel('');
      addNotification('Documento cargado.', 'success');
      await load();
    } catch (error: any) {
      addNotification(error?.message || 'Error al subir documento', 'error');
    }
  };

  const onUploadVersion = async (docId: string, file: File | null) => {
    if (!id || !file) return;
    try {
      await DeveloperDocumentsService.uploadNewVersion(id, docId, file);
      addNotification('Nueva versión cargada.', 'success');
      await load();
    } catch (error: any) {
      addNotification(error?.message || 'Error al subir versión', 'error');
    }
  };

  if (loading) return <div className={styles.loading}>Cargando...</div>;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <p className={styles.eyebrow}>Documentación</p>
          <h1 className={styles.title}>Documentación del proyecto</h1>
          <p className={styles.subtitle}>
            Sube la evidencia requerida y mantén cada archivo actualizado para su revisión.
          </p>
        </div>
        <div className={styles.headerActions}>
          <Link to="/developer/panel" className={styles.backButton}>
            Volver al panel
          </Link>
          <button className={styles.refreshButton} type="button" onClick={load}>
            Actualizar
          </button>
        </div>
      </header>

      <section className={styles.card}>
        <div className={styles.cardHeader}>
          <div>
            <h2 className={styles.cardTitle}>Subir documento</h2>
            <p className={styles.cardHint}>
              Sube todos los documentos requeridos para solicitar la revisión. "Otros documentos" es opcional.
            </p>
          </div>
          <span className={missingTypes.length > 0 ? styles.cardBadgeWarning : styles.cardBadge}>
            {submittedCount}/{requiredTypes.length} requeridos enviados
          </span>
        </div>

        {requiredDocsReady ? (
          <div className={styles.reviewNotice}>
            Ya enviaste los documentos requeridos. Un administrador los revisará; cuando sean aprobados, el proyecto se publicará automáticamente para los inversionistas.
          </div>
        ) : null}

        <div className={styles.requiredDocs}>
          {types.map(type => {
            const doc = documentByType.get(type.id);
            const key = (doc?.status || '').toUpperCase();
            const optional = isOptionalType(type);
            const label = optional
              ? (doc ? (statusLabels[key] || doc.status) : 'Opcional')
              : (doc ? (statusLabels[key] || doc.status) : 'Falta subir');
            return (
              <span
                key={type.id}
                className={`${styles.requiredDocTag} ${optional ? styles.requiredDocOptional : doc ? styles.requiredDocUploaded : styles.requiredDocMissing}`}
              >
                <strong>{type.label}</strong>
                <small>{label}</small>
              </span>
            );
          })}
        </div>

        <div className={styles.formGrid}>
          <label className={styles.field}>
            <span className={styles.label}>Tipo de documento</span>
            <select
              className={styles.select}
              value={typeId}
              onChange={e => {
                setTypeId(e.target.value);
                setCustomLabel('');
              }}
            >
              <option value="">Selecciona tipo</option>
              {types.map(t => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
          </label>

          {isOtherType ? (
            <label className={styles.field}>
              <span className={styles.label}>Especifica el documento</span>
              <input
                className={styles.textInput}
                type="text"
                value={customLabel}
                onChange={e => setCustomLabel(e.target.value)}
                placeholder="Ej. Avalúo comercial, planos estructurales..."
              />
            </label>
          ) : null}

          <label className={styles.field}>
            <span className={styles.label}>Archivo</span>
            <span className={styles.fileUpload}>
              <input
                className={styles.fileInput}
                type="file"
                accept={allowedAccept}
                onChange={e => {
                  const nextFile = validateFile(e.target.files?.[0] ?? null);
                  setFile(nextFile);
                  if (!nextFile) e.currentTarget.value = '';
                }}
              />
              <span className={styles.fileButton}>Seleccionar</span>
              <span className={styles.fileName}>
                {file?.name || 'Sin archivos seleccionados'}
              </span>
            </span>
          </label>

          <button
            className={styles.primaryButton}
            type="button"
            onClick={onUpload}
            disabled={!typeId || !file || selectedBlocked || (isOtherType && !customLabel.trim())}
          >
            Subir documento
          </button>

          {selectedBlocked ? (
            <div className={styles.helper}>Documento aprobado. No se permiten cambios.</div>
          ) : null}
          {!selectedBlocked && selectedType ? (
            <div className={styles.helper}>
              Tipo seleccionado: <strong>{selectedType.label}</strong>
            </div>
          ) : null}
        </div>
      </section>

      <section className={styles.card}>
        <div className={styles.cardHeader}>
          <div>
            <h2 className={styles.cardTitle}>Documentos cargados</h2>
            <p className={styles.cardHint}>Historial de documentos y versiones.</p>
          </div>
          <span className={styles.cardBadge}>{documents.length} registros</span>
        </div>
        {documents.length === 0 ? (
          <div className={styles.emptyState}>No hay documentos aún.</div>
        ) : (
          <div className={styles.documentsGrid}>
            {documents.map(doc => {
              const key = (doc.status || '').toUpperCase();
              const label = statusLabels[key] || doc.status;
              return (
                <div key={doc.id} className={styles.docCard}>
                  <div className={styles.docHeader}>
                    <div>
                      <h3 className={styles.docTitle}>{doc.type.label}</h3>
                      <span className={statusClass(doc.status)}>Estado: {label}</span>
                    </div>
                  </div>
                  <div className={styles.versions}>
                    {doc.versions?.map(v => (
                      <div key={v.id} className={styles.versionRow}>
                        <a href={resolveFileUrl(v.file_url)} target="_blank" rel="noreferrer">{v.file_name}</a>
                        <span>v{v.version}</span>
                      </div>
                    ))}
                  </div>
                  <div className={styles.versionUpload}>
                    <label className={styles.fileUpload}>
                      <input
                        className={styles.fileInput}
                        type="file"
                        accept={allowedAccept}
                        disabled={doc.status === 'APPROVED'}
                        onChange={e => {
                          const nextFile = validateFile(e.target.files?.[0] ?? null);
                          if (!nextFile) {
                            e.currentTarget.value = '';
                            return;
                          }
                          onUploadVersion(doc.id, nextFile);
                          e.currentTarget.value = '';
                        }}
                      />
                      <span className={styles.fileButton}>Nueva versión</span>
                      <span className={styles.fileName}>Sube un archivo actualizado</span>
                    </label>
                    {doc.status === 'APPROVED' ? (
                      <div className={styles.helper}>Documento aprobado. No se permiten cambios.</div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
